import { createStepFromJson, executeSteps, FormActionFrameworkStep, registerStep, } from '../step';
import { resolveTemplate } from '../template';
/**
 * Conditional container step.
 * Evaluates `condition`, then runs nested `thenSteps` or `elseSteps`.
 * The same context flows in and out (variables / formValues / shouldStop).
 */
export class IfStep extends FormActionFrameworkStep {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'if'
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                condition: '',
            }
        });
        Object.defineProperty(this, "thenSteps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "elseSteps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    async execute(context) {
        const raw = (typeof this.config.condition === 'string' && this.config.condition) || '';
        const result = evaluateCondition(raw, context);
        const branch = result ? 'then' : 'else';
        const steps = result ? this.thenSteps : this.elseSteps;
        context.log(`[if] condition → ${String(result)} — running ${branch} (${steps.length} step(s))`);
        if (steps.length === 0) {
            return context;
        }
        await executeSteps(steps, context);
        return context;
    }
    toJSON() {
        return {
            type: this.type,
            name: this.name || undefined,
            config: { ...this.config },
            then: this.thenSteps.map((s) => s.toJSON()),
            else: this.elseSteps.map((s) => s.toJSON()),
        };
    }
    fromJSON(json) {
        super.fromJSON(json);
        const record = json;
        this.thenSteps = Array.isArray(record.then)
            ? record.then.map((child) => createStepFromJson(child))
            : [];
        this.elseSteps = Array.isArray(record.else)
            ? record.else.map((child) => createStepFromJson(child))
            : [];
    }
    showStepForm(container, _context, onUpdated) {
        const cfg = this.config;
        container.innerHTML = `
      <div class="mb-3">
        <label class="form-label">Step name</label>
        <input type="text" class="form-control" data-faf="name" value="${escapeAttr(this.name)}" />
      </div>
      <div class="mb-3">
        <label class="form-label">Condition</label>
        <textarea class="form-control" data-faf="condition" rows="2" placeholder="expression or {{expression}}">${escapeText(String(cfg.condition ?? ''))}</textarea>
        <div class="text-muted small">
          Truthy → then branch; falsy → else. Supports {{ }} / formvalues / variables.
          Nested then/else steps are edited in the containers below.
        </div>
      </div>
    `;
        const sync = () => {
            this.name = inputValue(container, 'name');
            this.config.condition = textareaValue(container, 'condition');
            onUpdated?.();
        };
        container.querySelectorAll('input, textarea').forEach((el) => {
            el.addEventListener('change', sync);
            el.addEventListener('blur', sync);
        });
    }
}
/**
 * Evaluate a condition string to a boolean.
 * Accepts bare expressions (`formvalues.x === 1`) or mustache wrappers (`{{...}}`).
 */
export function evaluateCondition(condition, context) {
    const trimmed = condition.trim();
    if (!trimmed) {
        context.log('[if] empty condition — treated as false');
        return false;
    }
    try {
        const expr = trimmed.includes('{{') ? trimmed : `{{${trimmed}}}`;
        const value = resolveTemplate(expr, context);
        return isTruthy(value);
    }
    catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        context.log(`[if] condition error: ${message} — treated as false`);
        return false;
    }
}
function isTruthy(value) {
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return value !== 0 && !Number.isNaN(value);
    if (typeof value === 'string') {
        const s = value.trim().toLowerCase();
        if (!s || s === 'false' || s === '0' || s === 'null' || s === 'undefined') {
            return false;
        }
        return true;
    }
    return Boolean(value);
}
function inputValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value?.trim() ?? '';
}
function textareaValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value ?? '';
}
function escapeAttr(value) {
    if (value == null)
        return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
}
function escapeText(value) {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;');
}
registerStep('if', IfStep);
